import type { AbsenceStatus, AbsenceType, EmploymentType } from '@ip/shared-types';
import { getLanguage, t } from '@/i18n';

// ---------------------------------------------------------------------------
// Date helpers — all math is done on YYYY-MM-DD strings at UTC midnight so it
// mirrors the backend's working-days.util exactly; "today" alone is local.
// ---------------------------------------------------------------------------

export const MS_PER_DAY = 86_400_000;

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Local calendar date of "now" — NOT the UTC date (Poland is UTC+1/+2). */
export function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function parseYmd(ymd: string): Date {
  return new Date(`${ymd}T00:00:00Z`);
}

export function toYmd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(ymd: string, days: number): string {
  return toYmd(new Date(parseYmd(ymd).getTime() + days * MS_PER_DAY));
}

/** Inclusive list of YYYY-MM-DD dates. */
export function eachDay(from: string, to: string): string[] {
  const days: string[] = [];
  for (let ts = parseYmd(from).getTime(); ts <= parseYmd(to).getTime(); ts += MS_PER_DAY) {
    days.push(toYmd(new Date(ts)));
  }
  return days;
}

/** 0 = Monday … 6 = Sunday. */
export function isoWeekday(ymd: string): number {
  return (parseYmd(ymd).getUTCDay() + 6) % 7;
}

export function isWeekend(ymd: string): boolean {
  return isoWeekday(ymd) >= 5;
}

export function compareYmd(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function clampYmd(value: string, min: string, max: string): string {
  return value < min ? min : value > max ? max : value;
}

/** First day of the month containing `ymd`. */
export function monthStart(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

export function monthEnd(ymd: string): string {
  const d = parseYmd(monthStart(ymd));
  return toYmd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

export function addMonths(ymd: string, months: number): string {
  const d = parseYmd(monthStart(ymd));
  return toYmd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1)));
}

/**
 * Monday-first month matrix for a calendar: full weeks, each cell a
 * YYYY-MM-DD (cells outside the month included so rows are always 7 wide).
 */
export function monthMatrix(anyDayOfMonth: string): string[][] {
  const first = monthStart(anyDayOfMonth);
  const last = monthEnd(anyDayOfMonth);
  const gridStart = addDays(first, -isoWeekday(first));
  const gridEnd = addDays(last, 6 - isoWeekday(last));
  const all = eachDay(gridStart, gridEnd);
  const weeks: string[][] = [];
  for (let i = 0; i < all.length; i += 7) weeks.push(all.slice(i, i + 7));
  return weeks;
}

// ---------------------------------------------------------------------------
// Working-hours mirror of apps/backend/src/modules/hr/working-days.util.ts —
// weekends + public holidays cost 0 h; a half-day costs norm / 2. Used only
// for the LIVE preview in the request sheet; the backend stays authoritative.
// ---------------------------------------------------------------------------

export interface WorkingHoursOpts {
  halfDayStart?: boolean;
  halfDayEnd?: boolean;
  dailyNormHours?: number;
}

export function workingHoursBetween(
  from: string,
  to: string,
  holidays: ReadonlySet<string>,
  opts: WorkingHoursOpts = {},
): number {
  if (to < from) return 0;
  const norm = opts.dailyNormHours ?? 8;
  let hours = 0;
  for (const day of eachDay(from, to)) {
    if (isWeekend(day) || holidays.has(day)) continue;
    let dayHours = norm;
    if ((day === from && opts.halfDayStart) || (day === to && opts.halfDayEnd)) {
      dayHours = norm / 2;
    }
    hours += dayHours;
  }
  return hours;
}

// ---------------------------------------------------------------------------
// Display formatting
// ---------------------------------------------------------------------------

const round2 = (n: number) => Math.round(n * 100) / 100;

/** "13d 5h" / "13d" / "4h" / "0d" — hours → whole days at `norm` + remainder. */
export function formatDaysHours(hours: number, norm: number = 8): string {
  const sign = hours < 0 ? '-' : '';
  const abs = Math.abs(hours);
  const days = Math.floor(abs / norm + 1e-9);
  const rem = round2(abs - days * norm);
  if (days === 0 && rem === 0) return '0d';
  if (rem === 0) return `${sign}${days}d`;
  if (days === 0) return `${sign}${rem}h`;
  return `${sign}${days}d ${rem}h`;
}

/** Fractional days (e.g. 2.5) rendered compactly: "2,5 d" in pl, "2.5 d" in en. */
export function formatDays(days: number): string {
  const s = getLanguage() === 'pl' ? String(round2(days)).replace('.', ',') : String(round2(days));
  return `${s} d`;
}

function locale(): string {
  return getLanguage() === 'pl' ? 'pl-PL' : 'en-GB';
}

/** "15 lip" / "15 Jul" */
export function formatDateShort(ymd: string): string {
  return parseYmd(ymd).toLocaleDateString(locale(), { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** "15 lip 2026" */
export function formatDateMedium(ymd: string): string {
  return parseYmd(ymd).toLocaleDateString(locale(), {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

/** "15–18 lip" / "28 lip – 2 sie" (same-day ranges collapse to one date). */
export function formatDateRange(from: string, to: string): string {
  if (from === to) return formatDateMedium(from);
  if (from.slice(0, 7) === to.slice(0, 7)) {
    return `${parseYmd(from).getUTCDate()}–${formatDateMedium(to)}`;
  }
  return `${formatDateShort(from)} – ${formatDateMedium(to)}`;
}

/** "lipiec 2026" / "July 2026" */
export function formatMonthYear(ymd: string): string {
  return parseYmd(ymd).toLocaleDateString(locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Single-letter weekday headers, Monday first. */
export function weekdayLetters(): string[] {
  return getLanguage() === 'pl'
    ? ['P', 'W', 'Ś', 'C', 'P', 'S', 'N']
    : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
}

export function weekdayShort(): string[] {
  return getLanguage() === 'pl'
    ? ['pon', 'wt', 'śr', 'czw', 'pt', 'sob', 'nd']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * "Dawid Kuboszek" → "DK"; a single word gives its first two letters, an
 * empty/missing name "?". Takes one free-text name (as opposed to `initials`
 * above, for a first/last-name pair) — the avatar-fallback rule shared by
 * the CRM deal board and the navbar user menu.
 */
export function initialsFromName(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

// ---------------------------------------------------------------------------
// Absence type / status metadata — colours come exclusively from the palette
// already used by the app's chart tokens (indigo, emerald, amber, rose, cyan)
// + neutrals, in the same tint pattern as shared/StatusBadge.
// ---------------------------------------------------------------------------

export interface AbsenceTypeMeta {
  /** i18n leaf under hr.types.* */
  key: AbsenceType;
  /** Solid dot / bar colour. */
  dot: string;
  /** Chip (tinted pill) classes. */
  chip: string;
  /** Calendar-band classes (stronger tint, readable text). */
  band: string;
  /** Consumes the vacation pool. */
  pool: boolean;
  /** Auto-approved notification, never a request awaiting decision. */
  notifyOnly: boolean;
}

const emerald = {
  dot: 'bg-emerald-500',
  chip: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  band: 'bg-emerald-500/20 text-emerald-800 border-emerald-500/50 dark:text-emerald-300',
};
const cyan = {
  dot: 'bg-cyan-500',
  chip: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400',
  band: 'bg-cyan-500/20 text-cyan-800 border-cyan-500/50 dark:text-cyan-300',
};
const indigo = {
  dot: 'bg-indigo-500',
  chip: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400',
  band: 'bg-indigo-500/20 text-indigo-800 border-indigo-500/50 dark:text-indigo-300',
};
const amber = {
  dot: 'bg-amber-500',
  chip: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  band: 'bg-amber-500/20 text-amber-800 border-amber-500/50 dark:text-amber-300',
};
const rose = {
  dot: 'bg-rose-500',
  chip: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  band: 'bg-rose-500/20 text-rose-800 border-rose-500/50 dark:text-rose-300',
};
const neutral = {
  dot: 'bg-zinc-400 dark:bg-zinc-500',
  chip: 'border-zinc-400/40 bg-zinc-400/10 text-zinc-700 dark:text-zinc-300',
  band: 'bg-zinc-400/20 text-zinc-700 border-zinc-400/50 dark:text-zinc-300',
};

export const ABSENCE_TYPE_META: Record<AbsenceType, AbsenceTypeMeta> = {
  vacation: { key: 'vacation', ...emerald, pool: true, notifyOnly: false },
  vacation_on_demand: { key: 'vacation_on_demand', ...cyan, pool: true, notifyOnly: false },
  occasional: { key: 'occasional', ...indigo, pool: false, notifyOnly: false },
  childcare_art188: { key: 'childcare_art188', ...indigo, pool: false, notifyOnly: false },
  force_majeure_art148: { key: 'force_majeure_art148', ...indigo, pool: false, notifyOnly: false },
  care_art173: { key: 'care_art173', ...amber, pool: false, notifyOnly: false },
  unpaid: { key: 'unpaid', ...amber, pool: false, notifyOnly: false },
  sick: { key: 'sick', ...rose, pool: false, notifyOnly: true },
  unavailability_b2b: { key: 'unavailability_b2b', ...neutral, pool: false, notifyOnly: true },
  remote_work: { key: 'remote_work', ...cyan, pool: false, notifyOnly: true },
};

export function absenceTypeLabel(type: AbsenceType): string {
  return t(`hr.types.${type}`);
}

export function absenceTypeShort(type: AbsenceType): string {
  return t(`hr.typesShort.${type}`);
}

export const STATUS_CHIP: Record<AbsenceStatus, string> = {
  pending: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  approved: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  rejected: 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  cancelled: 'border-zinc-400/40 bg-zinc-400/10 text-zinc-600 dark:text-zinc-400',
};

export function absenceStatusLabel(status: AbsenceStatus): string {
  return t(`hr.status.${status}`);
}

/**
 * Types an employee may FILE, by contract type (mirrors AbsencesService.submit):
 * UOP → the statutory set + remote work; B2B/zlecenie/unset → unavailability +
 * remote work only ('sick' is never requestable — HR records e-ZLA).
 */
export function requestableTypes(employmentType: EmploymentType | null): AbsenceType[] {
  if (employmentType === 'employment_contract') {
    return [
      'vacation',
      'vacation_on_demand',
      'occasional',
      'childcare_art188',
      'care_art173',
      'force_majeure_art148',
      'unpaid',
      'remote_work',
    ];
  }
  return ['unavailability_b2b', 'remote_work'];
}

// ---------------------------------------------------------------------------
// CSV export (client-side; Excel-friendly: BOM + semicolons for pl locale)
// ---------------------------------------------------------------------------

export function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]): void {
  const sep = getLanguage() === 'pl' ? ';' : ',';
  const esc = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[";\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) => r.map(esc).join(sep)).join('\r\n');
  const blob = new Blob(['\ufeff', body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
